import { pointAPI } from '@/services-ts/axios'
import type {
  ICreateManualPoint,
  ICreatePointRule,
  IGetManualPointList,
  IGetPointHistory,
  IPaging,
  IUpdatePointSetting,
} from '@/types/Request'
import type { IPointStore } from '@/types/Store/State'
import { Alert, Format } from '@/utils-ts'
import { defineStore } from 'pinia'

export const usePointStore = defineStore('point', {
  state: (): IPointStore => ({
    pointRuleList: {
      data: [],
      total: 0,
    },
    pointSettings: null,

    manualPointList: {
      data: {
        items: [],
        excelFile: null,
      },
      total: 0,
    },
  }),

  actions: {
    async GetPointSettingList() {
      const { status, data } = await pointAPI.PointCreditServices.GetPointSettingList()
      if (status && Array.isArray(data)) {
        this.pointSettings = data[0] || null
      } else {
        this.pointSettings = null
      }
    },

    async UpdatePointSetting(payload: IUpdatePointSetting) {
      const { status } = await pointAPI.PointCreditServices.UpdatePointSetting(payload)

      if (status) {
        void Alert({ message: Format('admin_global_updated'), type: 'success' })
        await this.GetPointSettingList()
      }
    },

    async GetPointRuleList(payload: IPaging) {
      const { status, data, total } = await pointAPI.PointCreditServices.GetPointRuleList(payload)

      if (status && Array.isArray(data)) {
        data.forEach(item => {
          if (item.pointRuleDetailModels[0]!.filterName) {
            item.pointRuleTypeName = `${item.pointRuleTypeName}${
              item.pointRuleDetailModels[0]!.filterName
                ? ` (${item.pointRuleDetailModels[0]!.filterName})`
                : ''
            }`
          }
        })
        this.pointRuleList = status
          ? { data: data || [], total: total || 0 }
          : { data: [], total: 0 }
      }
    },

    async CreatePointRule(payload: ICreatePointRule) {
      const { status } = await pointAPI.PointCreditServices.CreatePointRule(payload)
      if (status) {
        void Alert({
          message: Format('admin_components_company_point_rule_pointcreated'),
          type: 'success',
        })
        await this.GetPointRuleList({ Skip: 0, Take: 10 })
      }
      return status
    },

    async DeletePointRule(Id: string) {
      const { status } = await pointAPI.PointCreditServices.DeletePointRule(Id)

      if (status) {
        this.pointRuleList.data = this.pointRuleList.data.filter(x => x.id !== Id)

        void Alert({ message: Format('admin_global_deleted'), type: 'success' })
      }
      return status
    },

    async GetManualPointList(payload: IGetManualPointList) {
      const { status, data, total } = await pointAPI.PointCreditServices.GetManualPointList(payload)
      this.manualPointList = status
        ? {
            data: { items: data?.items || [], excelFile: data?.excelFile || null },
            total: total || 0,
          }
        : { data: { items: [], excelFile: null }, total: 0 }
    },

    async GetManualPointListExcel(payload: IGetManualPointList) {
      await pointAPI.PointCreditServices.GetManualPointListExcel(payload)
    },

    async CreateManualPoint(payload: ICreateManualPoint) {
      const { status } = await pointAPI.PointCreditServices.CreateManualPoint(payload)
      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async ResetDistributablePoint() {
      const { status } = await pointAPI.PointCreditServices.ResetDistributablePoint()
      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async ResetSpendablePoints() {
      const { status } = await pointAPI.PointCreditServices.ResetSpendablePoints()
      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async GetPointHistory(payload: IGetPointHistory) {
      const { status, data, total } = await pointAPI.PointCreditServices.GetPointHistory(payload)
      return status ? { data, total: total || 0 } : { data: [], total: 0 }
    },
  },
})
